import React from "react";
import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  DefaultOptions,
  from,
  InMemoryCache,
  InMemoryCacheConfig,
  NormalizedCacheObject,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { getDataFromTree } from "@apollo/client/react/ssr";
import ApolloLinkTimeout from "apollo-link-timeout";
import { withApollo as nextWithApollo } from "next-with-apollo";

import { isClientError } from "@catchoftheday/utilities";

export type ApolloClientRetryOptions = {
  retryOptions?: RetryLink.Options & {
    retryTimeout: number;
  };
};

type WithApolloInput = ApolloClientRetryOptions & {
  uri: string | (() => string);
  cacheConfig?: InMemoryCacheConfig;
  defaultOptions?: DefaultOptions;
  connectToDevTools?: boolean;
};

type InitClientInput = {
  initialState: NormalizedCacheObject;
  ssrOrigin?: string;
} & WithApolloInput;

export function initClient({
  initialState,
  uri,
  ssrOrigin,
  cacheConfig,
  defaultOptions,
  connectToDevTools,
  retryOptions,
}: InitClientInput) {
  const apolloLink = () => {
    if (!retryOptions) {
      // If no retryOptions are configured, provide the uri
      return {
        uri,
      };
    }
    const { retryTimeout, attempts, ...otherOptions } = retryOptions;
    const timeoutLink = new ApolloLinkTimeout(retryTimeout);
    const errorLink = onError(({ networkError }) => {
      if (networkError && networkError.message === "Timeout exceeded") {
        // There's no elegant way to ignore specific network errors in Apollo Client v3
        // Implementing this workaround: https://github.com/apollographql/apollo-feature-requests/issues/153#issuecomment-513148734
        // Unset error message if the request has timed out in the timeoutLink so that it can be ignored in Sentry
        networkError.message = "";
      }
    });
    const httpLink = createHttpLink({
      uri,
    });
    const retryLink = new RetryLink({
      delay: {
        initial: 300,
        max: Infinity,
        jitter: true,
      },
      attempts: {
        max: 3,
        retryIf: (error) => {
          return !!error && !isClientError(error.statusCode);
        },
        ...(attempts || {}),
      },
      ...otherOptions,
    });

    return {
      link: from([retryLink, errorLink, timeoutLink, httpLink]),
    };
  };

  return new ApolloClient({
    name: process.env.NEXT_PUBLIC_PACKAGE,
    version: process.env.NEXT_PUBLIC_PACKAGE_VERSION,
    cache: new InMemoryCache(cacheConfig).restore(initialState),
    headers: {
      Accept: "application/json",
      ...(ssrOrigin ? { "x-forwarded-host": ssrOrigin } : undefined),
      ...(ssrOrigin ? { "x-ssr-origin": ssrOrigin } : undefined),
    },
    ...(defaultOptions && { defaultOptions }),
    connectToDevTools: !!connectToDevTools,
    ...apolloLink(),
  });
}

/**
 * Wraps a NextJS page with an Apollo provider and handles SSR data fetching
 */
export const withApollo = ({
  uri,
  cacheConfig,
  defaultOptions,
  connectToDevTools,
  retryOptions,
}: WithApolloInput) => {
  return nextWithApollo(
    ({ initialState, ctx }) => {
      const isLazyUri = typeof uri === "function";
      const isLegacyUri = typeof window !== "undefined" && !isLazyUri;
      const _uri = isLazyUri ? uri() : uri; // Allow uri to be fetched lazily
      const finalUri = isLegacyUri ? "/api" : _uri; // During the migration we need to support both the old and new uri styles.

      return initClient({
        initialState,
        uri: finalUri,
        ssrOrigin:
          ctx?.req?.headers.host ||
          (ctx?.req?.headers["x-forwarded-host"] as string),
        cacheConfig,
        defaultOptions,
        connectToDevTools,
        retryOptions,
      });
    },
    {
      getDataFromTree,
      render: function NextWithApolloWrapper({ Page, props }) {
        return (
          <ApolloProvider client={props.apollo}>
            <Page {...props} />
          </ApolloProvider>
        );
      },
    }
  );
};
